/**
 * TanStack Router — marketing-only.
 *
 * Изолированный routeTree для marketing.<домен>:
 *   /                      → MarketingPage
 *   /profile, /profile/$id → ProfilePage
 *   /login, /reset-password
 */
import {
  Outlet,
  Navigate,
  createRootRouteWithContext,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";

import { MarketingShell } from "@/components/MarketingShell";
import { useAuth, hasAccess } from "@/auth/AuthContext";
import { LoginPage } from "@/pages/LoginPage";
import { ResetPasswordPage } from "@/pages/ResetPasswordPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { MarketingPage } from "@/pages/MarketingPage";
import { MarketingSettingsPage } from "@/pages/MarketingSettingsPage";
import { MarketingAnalyticsPage } from "@/pages/MarketingAnalyticsPage";
import { MarketingAgentPage } from "@/pages/MarketingAgentPage";
import { CommercialPage, CommercialAnalyticsPage } from "@/pages/CommercialPage";


interface RouterContext { queryClient: QueryClient; }


function AuthGate() {
  const { user, isLoading, logout } = useAuth();
  if (isLoading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <div className="muted">Проверка сессии…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  // commercial.<домен> — коммерческий отчёт: доступ только владельцу (потом — через departments).
  const isCommercialHost = typeof window !== "undefined" && /^commercial\./i.test(window.location.hostname);
  const commercialOk = !!(user.is_superuser || (user as { role?: string }).role === "owner" || (user.departments ?? []).includes("commercial"));
  const denied = isCommercialHost ? !commercialOk : !hasAccess(user, "marketing");
  if (denied) {
    return (
      <div style={{
        minHeight: "100vh", display: "grid", placeItems: "center",
        background: "#f4f4f5", padding: 24, fontFamily: "Inter Tight, -apple-system, sans-serif",
      }}>
        <div style={{
          maxWidth: 440, background: "#fff", border: "1px solid #e6e6e6",
          borderRadius: 16, padding: "28px 32px", textAlign: "center",
          boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
        }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🔒</div>
          <h2 style={{ margin: "0 0 8px 0", fontSize: 18, fontWeight: 700, color: "#222" }}>
            {isCommercialHost ? "Коммерческий отчёт закрыт" : "Доступ к маркетингу закрыт"}
          </h2>
          <div style={{ fontSize: 13, color: "#707579", lineHeight: 1.5, marginBottom: 20 }}>
            {isCommercialHost
              ? "Коммерческий отчёт доступен только владельцу. Доступ другим выдаётся отдельно."
              : "Этот раздел доступен сотрудникам маркетинга. Если доступ нужен — попросите администратора добавить «marketing» в ваши департаменты."}
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
            <a
              href={(() => {
                const h = typeof window !== "undefined" ? window.location.hostname : "";
                return `https://${h.replace(/^marketing\./, "")}/`;
              })()}
              style={{
                padding: "8px 16px", background: "#7e57c2", color: "#fff",
                borderRadius: 8, fontSize: 13, fontWeight: 600, textDecoration: "none",
              }}
            >На главный дашборд</a>
            <button
              type="button"
              onClick={() => { void logout(); }}
              style={{
                padding: "8px 16px", background: "transparent", border: "1px solid #d4d4d8",
                color: "#222", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer",
              }}
            >Выйти</button>
          </div>
          <div style={{ marginTop: 16, fontSize: 11, color: "#9e9e9e" }}>
            Вы вошли как {user.email}
          </div>
        </div>
      </div>
    );
  }
  return <MarketingShell />;
}


function GuestGate() {
  const { user, isLoading } = useAuth();
  if (isLoading) return null;
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}


const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: GuestGate,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  component: AuthGate,
});

// На commercial.<домен> корень = коммерческий дашборд; иначе — маркетинг.
function HomeRoute() {
  const isCommercial = typeof window !== "undefined" && /^commercial\./i.test(window.location.hostname);
  return isCommercial ? <CommercialPage/> : <MarketingPage/>;
}
const indexRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: HomeRoute,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings",
  component: MarketingSettingsPage,
});

// На commercial.<домен> /analytics = коммерческая подробная аналитика; иначе — маркетинговая.
function AnalyticsRoute() {
  const isCommercial = typeof window !== "undefined" && /^commercial\./i.test(window.location.hostname);
  return isCommercial ? <CommercialAnalyticsPage/> : <MarketingAnalyticsPage/>;
}
const analyticsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/analytics",
  component: AnalyticsRoute,
});

const commercialRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/commercial",
  component: CommercialPage,
});

const agentRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/agent",
  component: MarketingAgentPage,
});

const profileRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile",
  component: ProfilePage,
});

const profileViewRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/profile/$userId",
  component: ProfilePage,
});


const routeTree = rootRoute.addChildren([
  loginRoute,
  resetPasswordRoute,
  protectedRoute.addChildren([
    indexRoute,
    settingsRoute,
    analyticsRoute,
    commercialRoute,
    agentRoute,
    profileRoute,
    profileViewRoute,
  ]),
]);


export function buildMarketingRouter(queryClient: QueryClient) {
  const rawBase = (import.meta.env.VITE_BASE_PATH as string | undefined) ?? "/";
  const basepath = rawBase === "/" ? undefined : rawBase.replace(/\/$/, "");
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: "intent",
    basepath,
  });
}
